import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Eye, Share2 } from 'lucide-react';
import ShareModal, { type ShareData } from '../components/ShareModal';
import { playClick } from '../utils/sounds';

export default function ReportPage() {
  const navigate = useNavigate();
  const { levelResults, contrastLevel, speedLevel, playerName, consecutiveDays } = useGameStore();
  const [shareData, setShareData] = useState<ShareData | null>(null);

  const latestResult = levelResults[levelResults.length - 1];
  const previousResults = levelResults.slice(-10, -1).filter(r => r.accuracyMeasured !== false && r.reactionTime > 0);

  const getAccuracyTrend = () => {
    if (previousResults.length < 2) return 'stable';
    const recent = previousResults.slice(-3).reduce((a, r) => a + r.accuracy, 0) / Math.min(3, previousResults.length);
    const earlier = previousResults.slice(0, 3).reduce((a, r) => a + r.accuracy, 0) / Math.min(3, previousResults.length);
    if (recent > earlier + 5) return 'up';
    if (recent < earlier - 5) return 'down';
    return 'stable';
  };

  const getReactionTrend = () => {
    if (previousResults.length < 2) return 'stable';
    const recent = previousResults.slice(-3).reduce((a, r) => a + r.reactionTime, 0) / Math.min(3, previousResults.length);
    const earlier = previousResults.slice(0, 3).reduce((a, r) => a + r.reactionTime, 0) / Math.min(3, previousResults.length);
    if (recent < earlier - 50) return 'up';
    if (recent > earlier + 50) return 'down';
    return 'stable';
  };

  const TrendIcon = ({ trend }: { trend: string }) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-gray-300" />;
  };

  const getPerformanceLabel = (accuracy: number) => {
    if (accuracy >= 90) return { text: '优秀', color: 'text-emerald-500' };
    if (accuracy >= 70) return { text: '良好', color: 'text-blue-500' };
    if (accuracy >= 50) return { text: '一般', color: 'text-amber-500' };
    return { text: '需加强', color: 'text-red-400' };
  };

  return (
    <div className="min-h-screen w-full bg-[var(--bg)] relative overflow-y-auto pb-20">
      {/* Header */}
      <header className="relative z-10 px-4 pt-6 pb-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg sticky top-0 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button onClick={() => navigate('/')} className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-base font-bold text-gray-800 dark:text-gray-100">训练报告</h1>
          {latestResult ? (
            <button
              onClick={() => {
                playClick();
                setShareData({
                  type: 'report',
                  title: '训练战绩',
                  playerName: playerName,
                  stats: [
                    { label: '得分', value: `${latestResult.score}` },
                    { label: '准确率', value: `${latestResult.accuracyMeasured === false ? '未测量' : `${Math.round(latestResult.accuracy)}%`}` },
                    { label: '反应时间', value: `${latestResult.reactionTime > 0 ? `${Math.round(latestResult.reactionTime)}ms` : '未测量'}` },
                  ],
                  summary: `在「${latestResult.levelName}」中获得了${latestResult.score}分，准确率${latestResult.accuracyMeasured === false ? '未测量' : `${Math.round(latestResult.accuracy)}%`}！`,
                });
              }}
              className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
            >
              <Share2 className="w-4 h-4" />
            </button>
          ) : (
            <Eye className="w-5 h-5 text-gray-300 dark:text-gray-600" />
          )}
        </div>
      </header>

      <div className="relative z-10 px-4 pt-4 max-w-lg mx-auto">
        {latestResult ? (
          <>
            {/* Latest Result */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm mb-3"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{latestResult.levelName}</h3>
                <span className="text-[10px] text-gray-400 bg-gray-50 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                  {new Date(latestResult.completedAt).toLocaleString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center bg-gray-50 dark:bg-gray-700 rounded-xl p-3">
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{latestResult.score}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">得分</p>
                </div>
                <div className="text-center bg-gray-50 dark:bg-gray-700 rounded-xl p-3">
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{latestResult.accuracyMeasured === false ? '未测量' : `${Math.round(latestResult.accuracy)}%`}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">准确率</p>
                </div>
                <div className="text-center bg-gray-50 dark:bg-gray-700 rounded-xl p-3">
                  <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{latestResult.reactionTime > 0 ? `${Math.round(latestResult.reactionTime)}ms` : '未测量'}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">反应时间</p>
                </div>
              </div>

              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 border border-emerald-100 dark:border-emerald-800">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">综合评估</span>
                  <span className={`text-sm font-bold ${getPerformanceLabel(latestResult.accuracy).color}`}>
                    {getPerformanceLabel(latestResult.accuracy).text}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Trends */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm mb-3"
            >
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">趋势分析</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">识别准确率</span>
                  <div className="flex items-center gap-2">
                    <TrendIcon trend={getAccuracyTrend()} />
                    <span className="text-xs text-gray-400">{getAccuracyTrend() === 'up' ? '提升中' : getAccuracyTrend() === 'down' ? '需注意' : '稳定'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">反应速度</span>
                  <div className="flex items-center gap-2">
                    <TrendIcon trend={getReactionTrend()} />
                    <span className="text-xs text-gray-400">{getReactionTrend() === 'up' ? '加快中' : getReactionTrend() === 'down' ? '需注意' : '稳定'}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Adaptive */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm mb-3"
            >
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">⚙️ 自适应调整</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500 dark:text-gray-400">色彩对比度</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">{Math.round(contrastLevel * 100)}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${contrastLevel * 100}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500 dark:text-gray-400">运动轨迹速度</span>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">{speedLevel.toFixed(1)}x</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(speedLevel / 2) * 100}%` }} />
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-3">系统根据你的表现自动调整后续关卡难度</p>
            </motion.div>

            {/* History */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm mb-3"
            >
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">最近训练</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {levelResults.slice(-8).reverse().map((result, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-xl p-3">
                    <div>
                      <p className="text-xs text-gray-700 dark:text-gray-200 font-medium">{result.levelName}</p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500">
                        {new Date(result.completedAt).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-emerald-600">{result.score}分</p>
                      <p className="text-[10px] text-gray-400">{Math.round(result.accuracy)}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Suggestions */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm"
            >
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">💡 训练建议</h3>
              <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                {latestResult.accuracy < 70 && <p>• 建议放慢速度，提高识别准确率再逐步加快</p>}
                {latestResult.reactionTime > 1000 && <p>• 反应时间偏长，可多练习萤火虫追踪提升反应速度</p>}
                {latestResult.accuracy >= 90 && <p>• 表现优秀！系统已自动提升难度以进一步训练</p>}
                <p>• 建议每日训练15-20分钟，保持稳定的训练频率</p>
                <p>• 训练后注意远眺放松，配合动态眼肌操效果更佳</p>
              </div>
            </motion.div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <Eye className="w-8 h-8 text-gray-300 dark:text-gray-500" />
            </div>
            <p className="text-gray-500 dark:text-gray-300 font-medium">还没有训练记录</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">完成任意关卡后这里会显示报告</p>
            <button onClick={() => navigate('/')} className="mt-6 px-6 py-3 rounded-xl bg-emerald-500 text-white font-semibold shadow-lg shadow-emerald-100 dark:shadow-emerald-900/20 hover:shadow-xl transition-all active:scale-95">
              开始训练
            </button>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 mx-auto sm:max-w-md md:max-w-lg sm:border-x z-40 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border-t border-gray-100 dark:border-gray-700">
        <div className="max-w-lg mx-auto flex items-center justify-center py-3">
          <button onClick={() => navigate('/')} className="px-8 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold shadow-lg shadow-emerald-100 active:scale-95 transition-all">
            返回首页
          </button>
        </div>
      </nav>

      {/* Share Modal */}
      <AnimatePresence>
        {shareData && (
          <ShareModal data={shareData} onClose={() => setShareData(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
